import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  Send, CheckCircle, ArrowRight, Clock, Star,
  AlertTriangle, TrendingUp, Target, Lock,
  Plus, Check, X, Copy, UserPlus, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { trackInsightSubmitted } from "../utils/analytics";
import { LinkedName, LinkedAvatar } from "../components/LinkedProfile";

const API = '/api';

const SWOT_STEPS = [
  {
    key: "strengths",
    label: "Strengths",
    step: 1,
    icon: Star,
    color: "#22c55e",
    bg: "bg-green-50",
    border: "border-green-200",
    headline: (name) => `What strengths do you see in ${name}?`,
    subtitle: (name) => `This is how ${name} sees their own strengths. React honestly — agree or push back.`,
    emptyMsg: (name) => `${name} hasn't added any strengths yet.`,
    addLabel: "WHAT WOULD YOU ADD?",
    addPlaceholder: (name) => `A strength ${name} might have missed...`,
  },
  {
    key: "weaknesses",
    label: "Weaknesses",
    step: 2,
    icon: AlertTriangle,
    color: "#ef4444",
    bg: "bg-red-50",
    border: "border-red-200",
    headline: (name) => `Where does ${name} need to improve?`,
    subtitle: () => `Real friends say this. That's why they asked you.`,
    emptyMsg: (name) => `${name} hasn't added any weaknesses yet.`,
    addLabel: "ADD A BLIND SPOT",
    addPlaceholder: (name) => `Something ${name} might not see about themselves...`,
  },
  {
    key: "opportunities",
    label: "Opportunities",
    step: 3,
    icon: TrendingUp,
    color: "#8b5cf6",
    bg: "bg-violet-50",
    border: "border-violet-200",
    headline: (name) => `What opportunities do you see for ${name}?`,
    subtitle: () => `What possibilities do you see for them — things they might be underselling or not yet seeing?`,
    emptyMsg: (name) => `${name} hasn't added any opportunities yet.`,
    addLabel: "ADD A POSSIBILITY",
    addPlaceholder: (name) => `An opportunity you see for ${name}...`,
  },
  {
    key: "threats",
    label: "Threats",
    step: 4,
    icon: Target,
    color: "#f59e0b",
    bg: "bg-amber-50",
    border: "border-amber-200",
    headline: (name) => `What external risks should ${name} watch out for?`,
    subtitle: () => `External pressures, patterns, or situations that could hold them back.`,
    emptyMsg: (name) => `${name} hasn't added any threats yet.`,
    addLabel: "ADD A THREAT",
    addPlaceholder: (name) => `Something that could get in ${name}'s way...`,
  },
];

const GiveInsightPage = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0=welcome, 1-4=SWOT steps, 5=confirmation, 6=grow network
  const [submitting, setSubmitting] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);
  const [alreadyReceivedInsight, setAlreadyReceivedInsight] = useState(false);
  const [sendingRequestBack, setSendingRequestBack] = useState(false);

  // Step 6: grow network state
  const [networkMilestone, setNetworkMilestone] = useState(null); // { current, target }
  const [inviteLink, setInviteLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("friend");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [skippedNetworkStep, setSkippedNetworkStep] = useState(false);

  // Per-step data: votes on existing items + new suggestions
  const [cardVotes, setCardVotes] = useState({});
  const [swotSuggestions, setSwotSuggestions] = useState({
    strengths: [], weaknesses: [], opportunities: [], threats: []
  });
  const [generalComment, setGeneralComment] = useState("");
  const [newItemInputs, setNewItemInputs] = useState({
    strengths: "", weaknesses: "", opportunities: "", threats: ""
  });

  // Update mode state
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [previousVoteItemIds, setPreviousVoteItemIds] = useState(new Set());
  const [hasPreviousVotes, setHasPreviousVotes] = useState(false);

  const fetchRequestData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/insights/request/${requestId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRequestData(data);
        try {
          const histRes = await fetch(`${API}/insights/requests`, { credentials: 'include' });
          if (histRes.ok) {
            const allRequests = await histRes.json();
            const hasPending = allRequests.some(r =>
              r.status === "pending" &&
              r.user_id === user?.user_id &&
              r.connection_id === data.connection?.connection_id
            );
            setAlreadyRequested(hasPending);
            // Check if user already received a completed insight from this person
            const hasCompleted = allRequests.some(r =>
              r.status === "completed" &&
              r.user_id === user?.user_id &&
              r.connection_id === data.connection?.connection_id
            );
            setAlreadyReceivedInsight(hasCompleted);
          }
        } catch {}
        // Always check for previous votes (even on pending requests)
        // so returning voters see the "update your view" screen
        try {
          const votesRes = await fetch(`${API}/insights/${requestId}/my-votes`, { credentials: 'include' });
          if (votesRes.ok) {
            const votesData = await votesRes.json();
            const prevVotes = votesData.votes || [];
            const ids = new Set(prevVotes.map(v => v.swot_item_id));
            setPreviousVoteItemIds(ids);

            // Flag that previous votes exist so the update screen shows
            if (votesData.has_previous_votes) {
              setHasPreviousVotes(true);
            }
          }
        } catch {}
      } else {
        toast.error('Failed to load request');
        navigate('/dashboard');
      }
    } catch {
      toast.error('Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [requestId, navigate, user?.user_id]);

  useEffect(() => { fetchRequestData(); }, [fetchRequestData]);

  const handleVote = (itemId, vote) => {
    setCardVotes(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        vote: prev[itemId]?.vote === vote ? null : vote
      }
    }));
  };

  const addSuggestion = (category) => {
    const text = newItemInputs[category]?.trim();
    if (!text) return false;
    setSwotSuggestions(prev => ({
      ...prev,
      [category]: [...prev[category], text]
    }));
    setNewItemInputs(prev => ({ ...prev, [category]: "" }));
    return true;
  };

  // Auto-capture pending input + show feedback toast on step advance
  const advanceStep = (currentStepKey, nextStepNum) => {
    // (a) Auto-add any text sitting in the input box
    const pendingText = newItemInputs[currentStepKey]?.trim();
    let addedExtra = false;
    if (pendingText) {
      setSwotSuggestions(prev => ({
        ...prev,
        [currentStepKey]: [...prev[currentStepKey], pendingText]
      }));
      setNewItemInputs(prev => ({ ...prev, [currentStepKey]: "" }));
      addedExtra = true;
    }

    // (b) Count what was captured and show feedback
    const votesOnStep = Object.entries(cardVotes).filter(([id]) => {
      const stepItems = (tool_data?.[currentStepKey] || []).filter(i => !i.deleted && !i.is_private);
      return stepItems.some(item => (item.item_id || item.id) === id);
    }).filter(([_, v]) => v.vote).length;
    const suggestionsOnStep = (swotSuggestions[currentStepKey]?.length || 0) + (addedExtra ? 1 : 0);

    if (votesOnStep > 0 || suggestionsOnStep > 0) {
      const parts = [];
      if (votesOnStep > 0) parts.push(`${votesOnStep} vote${votesOnStep > 1 ? 's' : ''}`);
      if (suggestionsOnStep > 0) parts.push(`${suggestionsOnStep} suggestion${suggestionsOnStep > 1 ? 's' : ''}`);
      toast.success(`${parts.join(' + ')} captured`, { duration: 1500 });
    }

    setStep(nextStepNum);
  };

  const removeSuggestion = (category, index) => {
    setSwotSuggestions(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const submitInsight = async () => {
    setSubmitting(true);
    try {
      const hasSwotItems = Object.values(swotSuggestions).some(arr => arr.length > 0);
      const hasVotes = Object.values(cardVotes).some(v => v.vote);

      const response = await fetch(`${API}/insights/${requestId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          insight_response: null,
          suggested_swot_items: hasSwotItems ? swotSuggestions : null,
          comment: null
        })
      });

      if (response.ok) {
        // Submit votes
        const voteEntries = Object.entries(cardVotes).filter(([_, v]) => v.vote);
        if (voteEntries.length > 0) {
          try {
            await fetch(`${API}/swot/votes`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                request_id: requestId,
                votes: voteEntries.map(([swot_item_id, v]) => ({
                  swot_item_id,
                  vote: v.vote,
                  comment: v.comment || null
                }))
              })
            });
          } catch {}
        }
        if (isUpdateMode) {
          const totalVotes = Object.values(cardVotes).filter(v => v.vote).length;
          const totalSuggestions = Object.values(swotSuggestions).reduce((s, arr) => s + arr.length, 0);
          trackInsightSubmitted({ votesCount: totalVotes, suggestionsCount: totalSuggestions, isUpdate: true });
          toast.success('Perspective updated!');
          setStep(5); // show confirmation with reciprocity CTA
        } else {
          const totalVotes = Object.values(cardVotes).filter(v => v.vote).length;
          const totalSuggestions = Object.values(swotSuggestions).reduce((s, arr) => s + arr.length, 0);
          trackInsightSubmitted({ votesCount: totalVotes, suggestionsCount: totalSuggestions, isUpdate: false });
          setStep(5); // confirmation
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const enterUpdateMode = async () => {
    // Fetch previous votes and pre-populate
    try {
      const res = await fetch(`${API}/insights/${requestId}/my-votes`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const prevVotes = {};
        const prevItemIds = new Set();
        for (const v of data.votes || []) {
          prevVotes[v.swot_item_id] = { vote: v.vote, comment: v.comment || '' };
          prevItemIds.add(v.swot_item_id);
        }
        setCardVotes(prevVotes);
        setPreviousVoteItemIds(prevItemIds);

        // Restore previous suggestions
        if (data.suggested_swot_items) {
          setSwotSuggestions({
            strengths: data.suggested_swot_items.strengths || [],
            weaknesses: data.suggested_swot_items.weaknesses || [],
            opportunities: data.suggested_swot_items.opportunities || [],
            threats: data.suggested_swot_items.threats || [],
          });
        }
      }
    } catch {}
    setIsUpdateMode(true);
    setStep(1);
  };

  const requestInsightBack = async () => {
    if (!requestData?.connection) return;
    setSendingRequestBack(true);
    try {
      const response = await fetch(`${API}/insights/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          connection_id: requestData.connection.connection_id,
          tool_type: requestData.request.tool_type || "swot",
          message: `I'd love your honest perspective on my SWOT`
        })
      });
      if (response.ok) {
        toast.success(`Request sent to ${requestData.requester.name}!`);
        await goToNetworkStepOrDashboard();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send request');
      }
    } catch {
      toast.error('Failed to send request');
    } finally {
      setSendingRequestBack(false);
    }
  };

  const goToNetworkStepOrDashboard = async () => {
    if (skippedNetworkStep) {
      navigate('/dashboard');
      return;
    }
    try {
      const res = await fetch(`${API}/dashboard/milestones`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const networkMs = data.milestones?.find(m => m.id === 'network');
        if (networkMs && !networkMs.complete) {
          setNetworkMilestone({ current: networkMs.current, target: networkMs.target });
          // Pre-generate invite link
          try {
            const linkRes = await fetch(`${API}/network/invite-link`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ tool_type: 'swot' })
            });
            if (linkRes.ok) {
              const linkData = await linkRes.json();
              const appUrl = window.location.origin;
              setInviteLink(`${appUrl}/api/share/invite/${linkData.invite_token}`);
            }
          } catch {}
          setStep(6);
          return;
        }
      }
    } catch {}
    navigate('/dashboard');
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const sendDirectInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      const res = await fetch(`${API}/network/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: inviteName.trim(),
          email: inviteEmail.trim(),
          role: inviteRole
        })
      });
      if (res.ok) {
        toast.success(`Invite sent to ${inviteName}!`);
        setInviteName("");
        setInviteEmail("");
        if (networkMilestone) {
          setNetworkMilestone(prev => ({
            ...prev,
            current: Math.min(prev.current + 1, prev.target)
          }));
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!requestData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-slate-600">Request not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  const { request, requester, tool_data } = requestData;
  const firstName = requester.name?.split(" ")[0] || requester.name;
  const canRespond = requestData.can_respond;
  const hasResponded = requestData.has_responded;

  // Count new items (items added since last vote)
  const countNewItems = () => {
    if ((!hasResponded && !hasPreviousVotes) || previousVoteItemIds.size === 0 || !tool_data) return 0;
    let count = 0;
    for (const cat of ['strengths', 'weaknesses', 'opportunities', 'threats']) {
      for (const item of tool_data[cat] || []) {
        const iid = item.item_id || item.id;
        if (!previousVoteItemIds.has(iid)) count++;
      }
    }
    return count;
  };

  // ── Already submitted or has previous votes — show update option ──
  if ((hasResponded || hasPreviousVotes) && !isUpdateMode && step === 0) {
    const newCount = countNewItems();
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="insight-already-submitted">
        <div className="max-w-md w-full">
          <div className="card p-8 text-center">
            {/* Avatar */}
            <div className="mb-6">
              <LinkedAvatar username={requester.username} className="inline-block">
                <Avatar className="h-20 w-20 mx-auto ring-4 ring-violet-100">
                  <AvatarImage src={requester.picture} alt={requester.name} />
                  <AvatarFallback className="bg-violet-100 text-violet-600 text-3xl font-bold">
                    {requester.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </LinkedAvatar>
            </div>

            {/* Success acknowledgment */}
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-full px-4 py-1.5 mb-5">
              <CheckCircle className="w-3.5 h-3.5" />
              You shared your perspective
            </div>

            <h2
              className="text-xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'system-ui, sans-serif' }}
              data-testid="update-headline"
            >
              Want to update your view?
            </h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Your votes and suggestions can be changed anytime. See something differently now? Go back in and update it.
            </p>

            {/* New items indicator */}
            {newCount > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3.5 mb-5 text-left flex items-center gap-3" data-testid="new-items-indicator">
                <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-violet-600">{newCount}</span>
                </div>
                <p className="text-sm text-violet-800">
                  {firstName} added <strong>{newCount} new item{newCount > 1 ? 's' : ''}</strong> since your last visit
                </p>
              </div>
            )}

            {/* Primary CTA — update */}
            <Button
              onClick={enterUpdateMode}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold mb-3"
              data-testid="update-perspective-btn"
            >
              Update my perspective
            </Button>

            {/* Secondary — go to dashboard */}
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="go-dashboard-btn"
            >
              Go to Dashboard <ArrowRight className="w-3.5 h-3.5 inline ml-0.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 0: Welcome ──
  if (step === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="insight-welcome">
        <div className="max-w-md w-full">
          <div className="card p-8 text-center">
            {/* Avatar */}
            <div className="mb-6">
              <LinkedAvatar username={requester.username} className="inline-block">
                <Avatar className="h-24 w-24 mx-auto ring-4 ring-violet-100">
                  <AvatarImage src={requester.picture} alt={requester.name} />
                  <AvatarFallback className="bg-violet-100 text-violet-600 text-3xl font-bold">
                    {requester.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </LinkedAvatar>
            </div>

            {/* Name */}
            <h1
              className="text-2xl font-bold text-slate-900 mb-1"
              style={{ fontFamily: 'system-ui, sans-serif' }}
              data-testid="welcome-name"
            >
              <LinkedName username={requester.username} name={firstName} testId="requester-profile-link" />
            </h1>
            {requester.username && (
              <a
                href={`/${requester.username}`}
                className="text-xs text-slate-500 hover:text-violet-600 block mb-3"
                data-testid="welcome-handle"
              >
                @{requester.username}
              </a>
            )}

            {/* Badge */}
            <span className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
              {firstName} asked for your honest view
            </span>

            {/* Headline */}
            <h2
              className="text-xl font-bold text-slate-900 mb-3 leading-snug"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              They trust you enough to ask how you really see them.
            </h2>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              {firstName} shared how they see themselves — their strengths, blind spots, and where they want to grow. Your honest perspective is the gift.
            </p>

            {/* Personal message */}
            {request.message && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 text-left" data-testid="personal-message">
                <div className="flex gap-2">
                  <span className="text-slate-300 text-lg font-serif leading-none">"</span>
                  <div>
                    <p className="text-sm text-slate-700 italic">{request.message}</p>
                    <p className="text-xs text-slate-400 mt-1.5">— {firstName}'s message to you</p>
                  </div>
                </div>
              </div>
            )}

            {/* Meta info */}
            <div className="flex items-center justify-center gap-3 text-xs text-slate-400 mb-6">
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> ~5 minutes</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> 4 questions</span>
            </div>
            <p className="flex items-center justify-center gap-1 text-[11px] text-slate-400 mb-6">
              <Lock className="w-3 h-3" /> Only {firstName} sees this
            </p>

            {/* CTA */}
            <Button
              onClick={() => setStep(1)}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold"
              data-testid="start-insight-btn"
            >
              Share my honest view <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 5: Confirmation ──
  if (step === 5) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="insight-confirmation">
        <div className="max-w-md w-full">
          <div className="card p-8 text-center">
            {/* Compact success banner */}
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-full px-4 py-1.5 mb-8" data-testid="confirmation-headline">
              <CheckCircle className="w-3.5 h-3.5" />
              Sent to {firstName}
            </div>

            {(alreadyRequested || alreadyReceivedInsight) ? (
              <>
                <h2
                  className="text-xl font-bold text-slate-900 mb-2"
                  style={{ fontFamily: 'system-ui, sans-serif' }}
                >
                  {alreadyReceivedInsight
                    ? `${firstName} already shared their view on you`
                    : `You already asked ${firstName} for their view`
                  }
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  {alreadyReceivedInsight
                    ? "You've both exchanged perspectives. Nice."
                    : "They'll be notified."
                  }
                </p>
                <Button
                  onClick={() => goToNetworkStepOrDashboard()}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold"
                  data-testid="go-dashboard-btn"
                >
                  {alreadyReceivedInsight ? 'Continue' : 'Go to Dashboard'}
                </Button>
              </>
            ) : (
              <div data-testid="request-back-section">
                {/* Main headline */}
                <h2
                  className="text-xl font-bold text-slate-900 mb-6"
                  style={{ fontFamily: 'system-ui, sans-serif' }}
                >
                  Now see how {firstName} sees you
                </h2>

                {/* Mock preview — what she'll receive */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 text-left">
                  <div className="flex items-start gap-3">
                    <LinkedAvatar username={requester.username} className="flex-shrink-0 mt-0.5">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={requester.picture} alt={requester.name} />
                        <AvatarFallback className="bg-violet-100 text-violet-600 text-sm font-bold">
                          {requester.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                    </LinkedAvatar>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 mb-1">
                        <LinkedName username={requester.username} name={firstName} />
                      </p>
                      <p className="text-sm text-slate-500 italic leading-relaxed">
                        "I'll share what I see in you — your strengths, blind spots, and what I think you're capable of."
                      </p>
                    </div>
                  </div>
                </div>

                {/* Primary CTA */}
                <Button
                  onClick={requestInsightBack}
                  disabled={sendingRequestBack}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold"
                  data-testid="request-back-btn"
                >
                  {sendingRequestBack ? 'Sending...' : `See how ${firstName} sees you`}
                </Button>

                {/* Zero-effort reassurance */}
                <p className="text-xs text-slate-400 mt-3">
                  {firstName} answers 4 quick questions. You don't need to do anything else.
                </p>

                {/* Dismiss */}
                <button
                  onClick={() => {
                    setSkippedNetworkStep(true);
                    goToNetworkStepOrDashboard();
                  }}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors mt-5 block mx-auto"
                  data-testid="maybe-later-btn"
                >
                  Maybe another time <ArrowRight className="w-3.5 h-3.5 inline ml-0.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Step 6: Grow Network ──
  if (step === 6 && networkMilestone) {
    const remaining = networkMilestone.target - networkMilestone.current;
    const progressPct = Math.round((networkMilestone.current / networkMilestone.target) * 100);
    const ROLES = [
      { value: 'friend', label: 'Friend' },
      { value: 'mentor', label: 'Mentor' },
      { value: 'coworker', label: 'Coworker' },
      { value: 'family', label: 'Family' },
      { value: 'coach', label: 'Coach' },
      { value: 'professor', label: 'Professor' },
      { value: 'business_partner', label: 'Business Partner' },
      { value: 'other', label: 'Other' },
    ];

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="grow-network-step">
        <div className="max-w-md w-full">
          <div className="card p-8">
            {/* Compact confirmation */}
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-full px-4 py-1.5 mb-6" data-testid="reciprocity-sent-banner">
              <CheckCircle className="w-3.5 h-3.5" />
              {firstName} is on it
            </div>

            {/* Headline */}
            <h2
              className="text-xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'system-ui, sans-serif' }}
              data-testid="grow-network-headline"
            >
              But one perspective isn't the full picture.
            </h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              A mentor sees potential. A friend sees patterns. A coworker sees strengths you take for granted. Who else should tell you how they see you?
            </p>

            {/* Progress ring */}
            <div className="flex items-center gap-4 bg-violet-50 border border-violet-100 rounded-xl p-4 mb-6" data-testid="network-progress">
              <div className="relative w-14 h-14 flex-shrink-0">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="24" fill="none" stroke="#e2e0f5" strokeWidth="4" />
                  <circle
                    cx="28" cy="28" r="24" fill="none"
                    stroke="#7c3aed" strokeWidth="4" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 24}`}
                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - progressPct / 100)}`}
                    className="transition-all duration-700"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-violet-700">
                  {networkMilestone.current}/{networkMilestone.target}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {remaining > 0
                    ? `${remaining} more perspective${remaining > 1 ? 's' : ''} to unlock your full picture`
                    : 'Milestone reached!'
                  }
                </p>
                <p className="text-xs text-slate-500 mt-0.5">People in your circle</p>
              </div>
            </div>

            {/* Share link — primary CTA */}
            {inviteLink && (
              <div className="mb-6" data-testid="share-link-section">
                <Button
                  onClick={copyInviteLink}
                  className={`w-full rounded-full h-12 text-sm font-semibold transition-all ${
                    linkCopied
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-violet-600 hover:bg-violet-700 text-white'
                  }`}
                  data-testid="copy-link-btn"
                >
                  {linkCopied ? (
                    <><Check className="w-4 h-4 mr-2" /> Link copied — share it!</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Copy your invite link</>
                  )}
                </Button>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Anyone who joins through this link will be asked to give you SWOT insights.
                </p>
              </div>
            )}

            {/* Inline invite form — secondary */}
            <div className="border-t border-slate-100 pt-5" data-testid="direct-invite-section">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <UserPlus className="w-3.5 h-3.5" />
                Or invite someone specific
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  placeholder="Their name"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200 transition-all"
                  data-testid="invite-name-input"
                />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="Their email"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200 transition-all"
                  data-testid="invite-email-input"
                />
                <div className="flex gap-2">
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-violet-300 bg-white"
                    data-testid="invite-role-select"
                  >
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <Button
                    onClick={sendDirectInvite}
                    disabled={sendingInvite || !inviteName.trim() || !inviteEmail.trim()}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-4 text-sm"
                    data-testid="send-invite-btn"
                  >
                    {sendingInvite ? '...' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Dismiss */}
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors mt-6 block mx-auto"
              data-testid="skip-network-btn"
            >
              I'm good for now <ArrowRight className="w-3.5 h-3.5 inline ml-0.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Steps 1-4: SWOT Quadrants ──
  const stepConfig = SWOT_STEPS[step - 1];
  const StepIcon = stepConfig.icon;
  const rawItems = tool_data?.[stepConfig.key] || [];
  const items = rawItems.filter(item => !item.deleted && !item.is_private);
  const suggestions = swotSuggestions[stepConfig.key];
  const isLastStep = step === 4;
  const nextStep = SWOT_STEPS[step]; // undefined if last

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid={`insight-step-${step}`}>
      <div className="max-w-md w-full">
        <div className="card p-6">
          {/* Progress bar */}
          <div className="flex gap-1.5 mb-6" data-testid="progress-bar">
            {SWOT_STEPS.map((s, i) => (
              <div
                key={s.key}
                className="h-1 flex-1 rounded-full transition-colors duration-300"
                style={{
                  backgroundColor: i < step ? stepConfig.color : "#e2e8f0"
                }}
              />
            ))}
          </div>

          {/* Category badge */}
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 mb-4 border"
            style={{
              color: stepConfig.color,
              borderColor: `${stepConfig.color}40`,
              backgroundColor: `${stepConfig.color}0a`
            }}
          >
            <StepIcon className="w-3.5 h-3.5" />
            {stepConfig.label} · {stepConfig.step} of 4
          </span>

          {/* Headline + subtitle */}
          <h2
            className="text-xl font-bold text-slate-900 mb-2 leading-snug"
            style={{ fontFamily: 'system-ui, sans-serif' }}
            data-testid="step-headline"
          >
            {stepConfig.headline(firstName)}
          </h2>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            {stepConfig.subtitle(firstName)}
          </p>

          {/* Existing items with vote buttons */}
          {items.length > 0 ? (
            <div className="space-y-3 mb-5">
              {items.map((item) => {
                const itemId = item.item_id || item.id;
                const itemText = item.content || item.text;
                const vote = cardVotes[itemId]?.vote;
                const isNewItem = isUpdateMode && previousVoteItemIds.size > 0 && !previousVoteItemIds.has(itemId);
                return (
                  <div
                    key={itemId}
                    className={`border rounded-xl p-4 ${isNewItem ? 'bg-violet-50/50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}
                    data-testid={`swot-item-${itemId}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2.5">
                      <p className="text-sm font-medium text-slate-800">{itemText}</p>
                      {isNewItem && (
                        <span className="text-[10px] font-semibold bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" data-testid={`new-badge-${itemId}`}>
                          New
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleVote(itemId, "agree")}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                          vote === "agree"
                            ? "bg-green-50 border-green-300 text-green-700"
                            : "border-slate-200 text-slate-500 hover:border-green-300 hover:text-green-600"
                        }`}
                        data-testid={`vote-agree-${itemId}`}
                      >
                        {vote === "agree" && <Check className="w-3 h-3" />}
                        This is true
                      </button>
                      <button
                        onClick={() => handleVote(itemId, "disagree")}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                          vote === "disagree"
                            ? "bg-red-50 border-red-300 text-red-700"
                            : "border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                        }`}
                        data-testid={`vote-disagree-${itemId}`}
                      >
                        {vote === "disagree" && <X className="w-3 h-3" />}
                        Disagree
                      </button>
                      <button
                        onClick={() => handleVote(itemId, "unsure")}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                          vote === "unsure"
                            ? "bg-slate-100 border-slate-400 text-slate-700"
                            : "border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-600"
                        }`}
                        data-testid={`vote-unsure-${itemId}`}
                      >
                        I'm not sure
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
              <p className="text-sm text-slate-400 italic">{stepConfig.emptyMsg(firstName)}</p>
            </div>
          )}

          {/* Add suggestion section */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
            <p className="text-xs font-bold text-slate-600 tracking-wide uppercase mb-2">
              {stepConfig.addLabel}
            </p>
            {/* Show added suggestions */}
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {suggestions.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border"
                    style={{
                      color: stepConfig.color,
                      borderColor: `${stepConfig.color}40`,
                      backgroundColor: `${stepConfig.color}0a`
                    }}
                  >
                    {s}
                    <button onClick={() => removeSuggestion(stepConfig.key, i)} className="hover:opacity-70">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newItemInputs[stepConfig.key]}
                onChange={(e) => setNewItemInputs(prev => ({ ...prev, [stepConfig.key]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addSuggestion(stepConfig.key)}
                placeholder={stepConfig.addPlaceholder(firstName)}
                className="flex-1 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-300 placeholder:text-slate-400"
                data-testid={`add-suggestion-input-${stepConfig.key}`}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => addSuggestion(stepConfig.key)}
                disabled={!newItemInputs[stepConfig.key]?.trim()}
                className="rounded-lg h-9 px-3 text-xs font-semibold"
                data-testid={`add-suggestion-btn-${stepConfig.key}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex flex-col items-center gap-3 mt-6">
            {isLastStep ? (
              <Button
                onClick={() => {
                  // Auto-capture pending input before submit
                  const pendingText = newItemInputs[stepConfig.key]?.trim();
                  if (pendingText) {
                    setSwotSuggestions(prev => ({
                      ...prev,
                      [stepConfig.key]: [...prev[stepConfig.key], pendingText]
                    }));
                    setNewItemInputs(prev => ({ ...prev, [stepConfig.key]: "" }));
                  }
                  // Small delay to let state update before submit
                  setTimeout(() => submitInsight(), 50);
                }}
                disabled={submitting}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold"
                data-testid="submit-insight-btn"
              >
                {submitting ? "Sending..." : (
                  <>
                    <Send className="w-4 h-4 mr-1.5" />
                    {isUpdateMode ? `Update & send to ${firstName}` : `Send to ${firstName}`}
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => advanceStep(stepConfig.key, step + 1)}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 text-sm font-semibold"
                  data-testid="next-step-btn"
                >
                  Next — {nextStep.label} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <button
                  onClick={() => setStep(prev => prev + 1)}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="skip-step-btn"
                >
                  Skip this one
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiveInsightPage;
